"use client";

import NavigationArrow from "@/assets/NavigationArrow";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";

type Props = PropsWithChildren<{
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  hideNavigation?: boolean;
  startIndex?: number;
  onNavigationClick?: (
    direction: "prev" | "next",
    currentIndex: number
  ) => void;
}>;

const Carousel6: React.FC<Props> = ({
  children,
  left_navigation_icon,
  left_navigation_icon_alt,
  right_navigation_icon,
  right_navigation_icon_alt,
  hideNavigation = false,
  startIndex = 0,
  onNavigationClick,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    startIndex,
  });

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      onNavigationClick?.("prev", newIndex);
    }
  }, [emblaApi, onNavigationClick]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      onNavigationClick?.("next", newIndex);
    }
  }, [emblaApi, onNavigationClick]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative w-full flex flex-col gap-6">
      {/* Viewport */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          {/* Slides */}
          <div className="flex">
            {React.Children.map(children, (child, index) => (
              <div
                key={index}
                className="flex-[0_0_25%] flex justify-center items-center"
              >
                {child}
              </div>
            ))}
          </div>
        </div>

        {/* Left gradient overlay - shows when can't scroll left */}
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 w-16 md:w-24 lg:w-32 pointer-events-none",
            "transition-opacity duration-300",
            prevBtnDisabled ? "opacity-100" : "opacity-0"
          )}
        />

        {/* Right gradient overlay - shows when can't scroll right */}
        <div
          className={cn(
            "absolute right-0 top-0 bottom-0 w-16 md:w-24 lg:w-32 pointer-events-none",
            "transition-opacity duration-300",
            nextBtnDisabled ? "opacity-100" : "opacity-0"
          )}
        />
      </div>

      {!hideNavigation && (
        <div className="flex gap-5 justify-center items-center">
          <div className="cursor-pointer" onClick={scrollPrev}>
            {left_navigation_icon && left_navigation_icon !== "" ? (
              <div
                className={cn(
                  "w-[36px] h-[36px]",
                  "sm:w-[38px] sm:h-[38px]",
                  "md:w-[42px] md:h-[42px]",
                  "lg:w-[56px] lg:h-[56px]",
                  emblaApi?.canScrollPrev() ? "opacity-100" : "opacity-30"
                )}
              >
                <Image
                  src={left_navigation_icon}
                  alt={left_navigation_icon_alt || "Previous"}
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <NavigationArrow
                fill={!prevBtnDisabled ? "#f74958" : "#F8B6BC"}
                pathFill={"white"}
                className={cn(
                  "xs:size-9",
                  "sm:size-[38px]",
                  "lg:size-[52px]",
                  "rotate-180"
                )}
              />
            )}
          </div>
          <div className="cursor-pointer" onClick={scrollNext}>
            {right_navigation_icon && right_navigation_icon !== "" ? (
              <div
                className={cn(
                  "w-[36px] h-[36px]",
                  "sm:w-[38px] sm:h-[38px]",
                  "md:w-[42px] md:h-[42px]",
                  "lg:w-[56px] lg:h-[56px]"
                )}
              >
                <Image
                  src={right_navigation_icon}
                  alt={right_navigation_icon_alt || "Next"}
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <NavigationArrow
                fill={!nextBtnDisabled ? "#f74958" : "#F8B6BC"}
                pathFill={"white"}
                className={cn("xs:size-9", "sm:size-[38px]", "lg:size-[52px]")}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel6;
